import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../styles/Home.css';

const Home = () => {
  // Animation variants
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  
  const fadeIn = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6
      }
    }
  };

  const blueprintContainerRef = useRef(null);
  const waveContainerRef = useRef(null);

  useEffect(() => {
    // Create background circles
    const createBackgroundCircles = () => {
      if (!waveContainerRef.current) return;
      
      const sizes = [300, 500, 700];
      
      sizes.forEach((size, index) => {
        const circle = document.createElement('div');
        circle.className = 'hero-circle';
        circle.style.width = `${size}px`;
        circle.style.height = `${size}px`;
        circle.style.left = `calc(70% - ${size/2}px)`;
        circle.style.top = `calc(50% - ${size/2}px)`;
        circle.style.animationDelay = `${index * 1.5}s`;
        
        waveContainerRef.current.appendChild(circle);
      });
    };
    
    // Create construction measurement lines
    const createMeasurementLines = () => {
      if (!blueprintContainerRef.current) return;
      
      const measurementTypes = [
        { className: 'vertical-measure-1 measurement' },
        { className: 'vertical-measure-2 measurement' },
        { className: 'horizontal-measure-1 measurement' },
        { className: 'horizontal-measure-2 measurement' }
      ];
      
      measurementTypes.forEach((measure) => {
        const line = document.createElement('div');
        line.className = measure.className;
        blueprintContainerRef.current.appendChild(line);
      });
      
      // Add construction tool icons
      const toolTypes = [
        { className: 'ruler construction-element' },
        { className: 'pencil construction-element' },
        { className: 'compass construction-element' },
        { className: 'hammer construction-element' },
        { className: 'helmet construction-element' }
      ];
      
      toolTypes.forEach((tool) => {
        const toolElement = document.createElement('div');
        toolElement.className = tool.className;
        blueprintContainerRef.current.appendChild(toolElement);
      });
      
      // Add blueprint text labels
      const labels = [
        { className: 'blueprint-label label-1', text: 'SECTION A-1' },
        { className: 'blueprint-label label-2', text: 'SCALE: 1:100' },
        { className: 'blueprint-label label-3', text: 'ELEVATION: 42.5m' },
        { className: 'blueprint-label label-4', text: 'REV: 2023-05' }
      ];
      
      labels.forEach(label => {
        const textElement = document.createElement('div');
        textElement.className = label.className;
        textElement.innerText = label.text;
        blueprintContainerRef.current.appendChild(textElement);
      });
      
      // Add technical blueprint elements
      const addTechnicalElements = () => {
        // Add dimension lines
        const dimensionLines = [
          { top: '25%', left: '20%', width: '150px', rotate: '0deg', text: '152.4cm' },
          { top: '65%', left: '30%', width: '120px', rotate: '90deg', text: '91.5cm' },
          { top: '45%', left: '70%', width: '100px', rotate: '45deg', text: '76.2cm' }
        ];
        
        dimensionLines.forEach((line, index) => {
          // Create dimension line
          const dimLine = document.createElement('div');
          dimLine.className = 'dimension-line';
          dimLine.style.top = line.top;
          dimLine.style.left = line.left;
          dimLine.style.width = line.width;
          dimLine.style.transform = `translateZ(30px) rotate(${line.rotate})`;
          blueprintContainerRef.current.appendChild(dimLine);
          
          // Create dimension text
          const dimText = document.createElement('div');
          dimText.className = 'dimension-text';
          dimText.innerText = line.text;
          dimText.style.top = line.top;
          dimText.style.left = line.left;
          dimText.style.transform = `translateZ(30px) translateY(-15px)`;
          blueprintContainerRef.current.appendChild(dimText);
        });
        
        // Add coordinate markers
        for (let i = 0; i < 6; i++) {
          for (let j = 0; j < 6; j++) {
            const marker = document.createElement('div');
            marker.className = 'dimension-text';
            marker.style.top = `${10 + i * 16}%`;
            marker.style.left = `${10 + j * 16}%`;
            marker.style.transform = 'translateZ(5px)';
            marker.innerText = `•${String.fromCharCode(65 + i)}${j+1}`;
            marker.style.fontSize = '8px';
            marker.style.opacity = '0.7';
            blueprintContainerRef.current.appendChild(marker);
          }
        }
        
        // Add technical notes
        const notes = [
          { top: '85%', left: '15%', text: 'NOTE: REINFORCE FOUNDATION' },
          { top: '15%', left: '75%', text: 'LOAD-BEARING WALL' },
          { top: '75%', left: '75%', text: 'MATERIAL: STEEL FRAME' }
        ];
        
        notes.forEach(note => {
          const noteElement = document.createElement('div');
          noteElement.className = 'dimension-text';
          noteElement.style.top = note.top;
          noteElement.style.left = note.left;
          noteElement.style.transform = 'translateZ(20px)';
          noteElement.innerText = note.text;
          noteElement.style.opacity = '0.9';
          blueprintContainerRef.current.appendChild(noteElement);
        });
      };
      
      // Add blueprint technical elements
      const addBlueprintElements = () => {
        // Add dotted pattern
        const dottedPattern = document.createElement('div');
        dottedPattern.className = 'blueprint-dotted-pattern';
        blueprintContainerRef.current.appendChild(dottedPattern);
        
        // Add circles
        const circles = [
          { top: '50%', left: '50%', size: '200px' },
          { top: '50%', left: '50%', size: '100px' },
          { top: '30%', left: '70%', size: '80px' }
        ];
        
        circles.forEach(circle => {
          const circleElement = document.createElement('div');
          circleElement.className = 'blueprint-circle';
          circleElement.style.top = circle.top;
          circleElement.style.left = circle.left;
          circleElement.style.width = circle.size;
          circleElement.style.height = circle.size;
          circleElement.style.marginLeft = `-${parseInt(circle.size)/2}px`;
          circleElement.style.marginTop = `-${parseInt(circle.size)/2}px`;
          blueprintContainerRef.current.appendChild(circleElement);
        });
        
        // Add diagonal lines
        const diagonals = [
          { top: '30%', left: '20%', length: '400px', angle: '45deg' },
          { top: '70%', left: '20%', length: '350px', angle: '-30deg' }
        ];
        
        diagonals.forEach(line => {
          const diagonal = document.createElement('div');
          diagonal.className = 'blueprint-diagonal';
          diagonal.style.top = line.top;
          diagonal.style.left = line.left;
          diagonal.style.width = line.length;
          diagonal.style.transform = `translateZ(10px) rotate(${line.angle})`;
          blueprintContainerRef.current.appendChild(diagonal);
        });
        
        // Add grid markers
        for (let i = 0; i < 10; i++) {
          const marker = document.createElement('div');
          marker.className = 'blueprint-grid-marker';
          marker.style.top = `${10 + i * 10}%`;
          marker.style.left = '10%';
          marker.style.transform = 'translateZ(15px)';
          blueprintContainerRef.current.appendChild(marker);
          
          const marker2 = document.createElement('div');
          marker2.className = 'blueprint-grid-marker';
          marker2.style.top = '10%';
          marker2.style.left = `${10 + i * 10}%`;
          marker2.style.transform = 'translateZ(15px)';
          blueprintContainerRef.current.appendChild(marker2);
        }
        
        // Add axes
        const xAxis = document.createElement('div');
        xAxis.className = 'blueprint-axis x-axis';
        blueprintContainerRef.current.appendChild(xAxis);
        
        const yAxis = document.createElement('div');
        yAxis.className = 'blueprint-axis y-axis';
        blueprintContainerRef.current.appendChild(yAxis);
      };
      
      addTechnicalElements();
      addBlueprintElements();
    };
    
    createBackgroundCircles();
    createMeasurementLines();
    
    return () => {
      if (blueprintContainerRef.current) {
        blueprintContainerRef.current.innerHTML = '';
      }
      if (waveContainerRef.current) {
        waveContainerRef.current.innerHTML = '';
      }
    };
  }, []);

  const features = [
    {
      title: 'Material Cost Analysis',
      description: 'AI-powered material calculations with real-time lumber yard pricing integration and waste reduction algorithms.',
      icon: '📐',
      bgColor: 'bg-primary-900/10',
      iconBg: 'bg-primary-900/20',
      iconColor: 'text-primary-400',
      measurements: ['Accuracy: 99.8%', 'Update Rate: 4hrs', 'Data Points: 1.2M+']
    },
    {
      title: 'Labor Cost Tracking',
      description: 'Comprehensive labor cost monitoring including payroll taxes, workers comp, and benefits calculations.',
      icon: '⚙️',
      bgColor: 'bg-secondary-900/10',
      iconBg: 'bg-secondary-900/20',
      iconColor: 'text-secondary-400',
      measurements: ['Precision: ±0.5%', 'Real-time', 'Multi-crew Support']
    },
    {
      title: 'Project Analytics',
      description: 'Advanced analytics dashboard with profit margin tracking, cost breakdowns, and performance metrics.',
      icon: '📊',
      bgColor: 'bg-indigo-900/10',
      iconBg: 'bg-indigo-900/20',
      iconColor: 'text-indigo-400',
      measurements: ['ROI Tracking', 'Margin Analysis', '30+ Metrics']
    },
    {
      title: 'Overhead Calculator',
      description: 'Smart overhead expense allocation with automated distribution and cost center tracking.',
      icon: '🔧',
      bgColor: 'bg-emerald-900/10',
      iconBg: 'bg-emerald-900/20',
      iconColor: 'text-emerald-400',
      measurements: ['Auto-scaling', 'Dynamic Allocation', 'Cost Centers: 12']
    }
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        {/* Background elements */}
        <div className="hero-background absolute inset-0 bg-gradient-to-br from-primary-900 to-secondary-900">
          <div ref={waveContainerRef} className="wave-container"></div>
        </div>
        
        {/* Blueprint and Building Animation */}
        <div ref={blueprintContainerRef} className="blueprint-container">
          <div className="blueprint-grid">
            <div className="blueprint-base"></div>
            <div className="building-container">
              <div className="floor floor-1"></div>
              <div className="floor floor-2"></div>
              <div className="floor floor-3"></div>
              <div className="floor floor-4"></div>
              <div className="floor floor-5"></div>
              <div className="floor floor-6"></div>
              <div className="floor floor-7"></div>
            </div>
            
            {/* Construction Crane */}
            <div className="crane">
              <div className="crane-base"></div>
              <div className="crane-arm"></div>
              <div className="crane-cable"></div>
              <div className="crane-hook"></div>
            </div>
          </div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="max-w-xl mx-auto text-center md:text-left md:mr-auto md:ml-0"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div
              variants={fadeIn}
              className="mb-8 flex md:justify-start justify-center"
            >
              <div className="animated-logo-3d">
                <div className="logo-cube">
                  <div className="cube-face front"></div>
                  <div className="cube-face back"></div>
                  <div className="cube-face right"></div>
                  <div className="cube-face left"></div>
                  <div className="cube-face top"></div>
                  <div className="cube-face bottom"></div>
                </div>
                <div className="logo-gears">
                  <div className="gear gear-1"></div>
                  <div className="gear gear-2"></div>
                  <div className="gear gear-3"></div>
                </div>
                <div className="logo-text">NTC</div>
              </div>
            </motion.div>
            <motion.h1 
              variants={fadeIn}
              className="text-5xl md:text-7xl font-bold text-white leading-tight mb-6"
            >
              Empowering Decking Businesses
            </motion.h1>
            <motion.p 
              variants={fadeIn}
              className="text-xl md:text-2xl text-white/90 mb-12 max-w-xl"
            >
              The cutting-edge solution designed exclusively for the decking industry, delivering precision in cost calculations and enhanced profitability.
            </motion.p>
            <motion.div 
              variants={fadeIn}
              className="flex flex-col sm:flex-row justify-center md:justify-start gap-6"
            >
              <Link 
                to="/pricing" 
                className="px-8 py-4 border-2 border-white/30 text-white hover:bg-white/10 rounded-full font-bold text-lg transition-all duration-300"
              >
                View Pricing
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-3 py-1 bg-primary-900/20 text-primary-400 rounded-full text-sm font-medium mb-4 border border-primary-700/30">
              Core Features
            </span>
            <h2 className="text-4xl font-bold mb-4 text-white">Technical Precision Tools</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Industry-specific solutions engineered for the decking sector's unique requirements.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`${feature.bgColor} p-8 rounded-2xl border border-gray-700/50 backdrop-blur-sm relative group hover:border-primary-500/50 transition-all duration-300`}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-transparent to-primary-900/10 rounded-2xl"></div>
                <div className={`${feature.iconBg} w-16 h-16 flex items-center justify-center text-3xl rounded-2xl mb-6 ${feature.iconColor} relative`}>
                  {feature.icon}
                  <div className="absolute inset-0 border border-gray-700/50 rounded-2xl"></div>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white relative">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed relative mb-6">{feature.description}</p>
                <div className="space-y-2 relative">
                  {feature.measurements.map((measurement, idx) => (
                    <div key={idx} className="flex items-center text-sm text-primary-400">
                      <span className="w-2 h-2 bg-primary-500 rounded-full mr-2"></span>
                      {measurement}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      {/* TODO: Load approved review from Firestore - fetch from 'reviews' collection where approved === true */}
      <section className="py-24 bg-gradient-to-br from-primary-900 to-secondary-900 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '30px 30px'
          }}></div>
        </div>
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto relative"
          >
            <div className="absolute -left-4 -top-4 w-12 h-12 border-l-2 border-t-2 border-primary-400"></div>
            <div className="absolute -right-4 -bottom-4 w-12 h-12 border-r-2 border-b-2 border-primary-400"></div>
            <h2 className="text-4xl font-bold text-center mb-16">Technical Excellence Verified</h2>
            <div className="bg-primary-800/50 p-10 rounded-3xl backdrop-blur-sm border border-primary-700/50">
              <div className="text-center py-12">
                <p className="text-xl text-gray-300 leading-relaxed">
                  No reviews available yet. Approved reviews will appear here.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gray-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: 'linear-gradient(30deg, rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(150deg, rgba(255,255,255,.1) 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="container mx-auto px-4 text-center relative"
        >
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">Ready to Optimize Your Operations?</h2>
            <p className="text-xl max-w-2xl mx-auto mb-12 text-gray-400">
              Join the network of decking professionals leveraging technical precision for enhanced profitability.
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block"
            >
              <Link 
                to="/demo" 
                className="group relative px-12 py-4 rounded-full font-bold text-xl inline-block overflow-hidden"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-primary-600 to-secondary-600"></span>
                <span className="absolute inset-0 bg-gradient-to-r from-primary-500 to-secondary-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                <span className="relative text-white flex items-center">
                  Get Started Today
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default Home; 